import React, { useEffect } from "react";
import { Modal, Form, Input, Switch } from "antd";
import { useFeeTypes } from "../../../hooks/finance/useFeeTypes";
import { generateCodeFromName } from "../../../utils/codeUtils";

const FeeTypeModal = ({ visible, onCancel, onSuccess, feeType }) => {
  const [form] = Form.useForm();
  const { createFeeType, updateFeeType } = useFeeTypes();
  const isEditing = !!feeType;

  useEffect(() => {
    if (visible && feeType) {
      form.setFieldsValue({
        name: feeType.name,
        is_tuition: feeType.isTuition === true,
        description: feeType.description,
        is_active: feeType.isActive === 1,
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, feeType, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const normalizedName = values.name?.trim() || "";
      const shouldKeepExistingCode =
        isEditing && normalizedName === (feeType?.name || "").trim();
      const autoCode = generateCodeFromName(normalizedName);

      const finalCode =
        shouldKeepExistingCode && feeType?.code
          ? feeType.code
          : autoCode || feeType?.code || "";

      const data = {
        name: values.name,
        code: finalCode,
        description: values.description || null,
        isTuition: values.is_tuition ? true : false, // Send camelCase
        isActive: values.is_active ? 1 : 0, // Send camelCase
      };

      let result;
      if (isEditing) {
        result = await updateFeeType(feeType.feeTypeId, data);
      } else {
        result = await createFeeType(data);
      }

      if (result) {
        onSuccess();
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Modal
      title={isEditing ? "Edit Fee Type" : "Create Fee Type"}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={isEditing ? "Update" : "Create"}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          is_tuition: false,
          is_active: true,
        }}
      >
        <Form.Item
          label="Fee Type Name"
          name="name"
          rules={[
            { required: true, message: "Please enter fee type name" },
            { max: 100, message: "Name must be less than 100 characters" },
          ]}
          tooltip="The display name for this fee type (e.g., Tuition, Boarding)"
        >
          <Input placeholder="e.g., Tuition, Boarding" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          tooltip="Optional detailed description of what this fee covers"
        >
          <Input.TextArea
            rows={3}
            placeholder="e.g., School tuition fees for academic year"
            showCount
            maxLength={500}
          />
        </Form.Item>

        <Form.Item
          label="Is Tuition Fee"
          name="is_tuition"
          valuePropName="checked"
          tooltip="Enable this if this is the main school tuition fee. Tuition fees are typically mandatory and charged per term."
          extra="Tuition fees are treated differently in payment allocation and reporting."
        >
          <Switch checkedChildren="Yes" unCheckedChildren="No" />
        </Form.Item>

        <Form.Item
          label="Active Status"
          name="is_active"
          valuePropName="checked"
          tooltip="Only active fee types can be used in fee structures"
          extra="Inactive fee types will not appear in fee structure creation."
        >
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FeeTypeModal;
